import languages from '@pp/languages';

import type { DictionaryMap, DictionaryTags } from '../services/dictionary/types';

export interface ITranslationConfig {
  /** Provide codes of languages not supported by your product. Keep null if non-applicable. List of all languages: /libraries/languages */
  excludedLanguages: string[] | null;
  /** Provide Lokalise tags. E.g., 'core', 'table_name', 'promo', 'topcard' */
  uiFiles: DictionaryTags;
  /** Optional. A map of keys to keys. E.g., 'BALANCE' -> 'CREDIT'. */
  uiDictionaryMap?: DictionaryMap;
  /** Provide Lokalise help file names. E.g., 'common', 'topcard'. */
  helpFiles?: DictionaryTags;
  /** Optional. A map of keys to keys. E.g., 'BALANCE' -> 'CREDIT'. */
  helpDictionaryMap?: DictionaryMap;
}

export const DEFAULT_LANGUAGE_CODE = languages.default; // "en"

/**
 * Returns parsed language from browser's locale which are in format "de-DE", "ja-JP", "zh-Hans-CN", etc.
 */
export const parseBrowserLocale = (locale: string): string => locale.split('-')[0] ?? locale;

/**
 * Determines if language code is among supported localisations.
 */
const isLanguageAvailable = (languageCode: string, excludedLanguages: string[]): boolean => {
  return languageCode in languages.available && !excludedLanguages.includes(languageCode);
};

/**
 * Retrieves the language based on the provided configuration and session language code.
 * If localisation is not supported, it falls back to browser's language, then to default language.
 */
export const getLanguage = (translationConfig: ITranslationConfig, sessionLanguageCode: string | undefined): string => {
  const excludedLanguages = translationConfig.excludedLanguages || [];
  const sessionLanguageLowerCase = sessionLanguageCode?.toLowerCase();

  // Check if the session language code is available and not excluded
  if (sessionLanguageLowerCase && isLanguageAvailable(sessionLanguageLowerCase, excludedLanguages)) {
    return sessionLanguageLowerCase;
  }

  // Fallback to the browser's language
  const browserLanguage = parseBrowserLocale(navigator.language);

  if (browserLanguage && isLanguageAvailable(browserLanguage, excludedLanguages)) {
    return browserLanguage;
  }

  // Fallback to the default language
  return DEFAULT_LANGUAGE_CODE;
};

/**
 * Determines if the given language code corresponds to a Right-to-Left (RTL) language.
 */
export const getIsLanguageRTL = (languageCode: string): boolean => languages.rtl.includes(languageCode);
